import { ENABLED_INTEGRATIONS_STORAGE_KEY } from "../constants/local-storage";
import type {
  IEnabledIntegrationProviderInfo,
  IEnabledIntegrationsResponse,
} from "../types/integration";
import { EIntegrationProvider } from "../types/integration";
import type { IStorageAdapter } from "./storage";

// NOTE: NSAI-1249 — the integrations half of the feature-flag system, lifted out
// of web so mobile can use the same rule instead of hiding every FF integration
// outright (which is what it did before this file existed).
//
// The registry in @frontend-shared/constants/feature-flags cannot describe these
// as static entries: which providers are behind ENABLED_INTEGRATIONS_FF is a
// deployment's own decision, arriving in the /integrations/enabled response
// rather than being known at build time. So the opt-in is a list of provider
// ids rather than a boolean, stored as one comma-separated value under the same
// key web has always used.

// NOTE: built once — the enum is large, and this runs on every read.
const INTEGRATION_PROVIDERS: ReadonlySet<string> = new Set<string>(
  Object.values(EIntegrationProvider),
);

const isIntegrationProvider = (value: string): value is EIntegrationProvider =>
  INTEGRATION_PROVIDERS.has(value);

export const readEnabledIntegrationOverrides = (
  adapter: IStorageAdapter,
): EIntegrationProvider[] => {
  const stored = adapter.getItem(ENABLED_INTEGRATIONS_STORAGE_KEY);

  if (!stored) {
    return [];
  }

  // NOTE: validated against the enum rather than asserted onto it. The value is
  // typed by hand — a developer editing DevTools or the mobile dev screen — so
  // "whatever is in storage is a provider" is exactly the assumption that does
  // not hold, and the repo's no-type-assertion rule exists for this boundary.
  // A stale id from a since-removed integration used to survive as a phantom
  // provider; it now drops out here rather than being believed downstream.
  return stored
    .split(",")
    .map((item) => item.trim())
    .filter(isIntegrationProvider);
};

export interface IWriteEnabledIntegrationOverridesProps {
  adapter: IStorageAdapter;
  providers: readonly EIntegrationProvider[];
}

export const writeEnabledIntegrationOverrides = ({
  adapter,
  providers,
}: IWriteEnabledIntegrationOverridesProps): void => {
  if (providers.length === 0) {
    adapter.removeItem(ENABLED_INTEGRATIONS_STORAGE_KEY);
    return;
  }

  adapter.setItem(ENABLED_INTEGRATIONS_STORAGE_KEY, providers.join(","));
};

export interface IFilterEnabledIntegrationsProps {
  response: IEnabledIntegrationsResponse;
  enabledFromStorage: readonly EIntegrationProvider[];
}

export const filterEnabledIntegrations = ({
  response,
  enabledFromStorage,
}: IFilterEnabledIntegrationsProps): IEnabledIntegrationProviderInfo[] => {
  const { enabled, enabledFF } = response;

  // NOTE: ENABLED_INTEGRATIONS_FF takes precedence over ENABLED_INTEGRATIONS.
  // A provider in both lists must stay gated by the opt-in, so we drop it from
  // `enabled` before merging.
  const ffSet = new Set(enabledFF.map((info) => info.provider));
  const enabledOnly = enabled.filter((info) => !ffSet.has(info.provider));

  const filteredEnabledFF = enabledFF.filter((info) =>
    enabledFromStorage.includes(info.provider),
  );

  return [...enabledOnly, ...filteredEnabledFF];
};
